import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import jwt_decode from 'jwt-decode';

export interface CacheItem<T> {
  data: T;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private cache = new Map<string, CacheItem<any>>();
  private readonly DEFAULT_TTL = 2 * 60 * 1000; // 2 minutes
  private currentLocationId: number | null = null;

  constructor() {
    this.loadLocationId();
  }

  /**
   * Load locationId from JWT token
   */
  private async loadLocationId(): Promise<void> {
    try {
      const token = await Preferences.get({ key: 'access-token' });
      if (token && token.value) {
        const decoded: any = jwt_decode(token.value);
        this.currentLocationId = decoded.LocationId || null;
      }
    } catch (error) {
      console.error('Error loading locationId from token:', error);
      this.currentLocationId = null;
    }
  }

  /**
   * Update location ID (call this when location changes)
   */
  async updateLocationId(): Promise<void> {
    await this.loadLocationId();
  }

  /**
   * Get current location ID
   */
  getLocationId(): number | null {
    return this.currentLocationId;
  }

  /**
   * Create location-aware cache key
   */
  private getLocationKey(key: string): string {
    if (this.currentLocationId === null) {
      return key; // Fallback to non-location key if no location
    }
    return `loc:${this.currentLocationId}:${key}`;
  }

  /**
   * Get cached data if it exists and hasn't expired
   */
  get<T>(key: string, ttl: number = this.DEFAULT_TTL): T | null {
    const locationKey = this.getLocationKey(key);
    const item = this.cache.get(locationKey);

    if (!item) {
      return null;
    }

    const age = Date.now() - item.timestamp;

    // Check if cache has expired
    if (age > ttl) {
      this.cache.delete(locationKey);
      return null;
    }

    return item.data as T;
  }

  /**
   * Set data in cache with current timestamp
   */
  set<T>(key: string, data: T): void {
    const locationKey = this.getLocationKey(key);
    this.cache.set(locationKey, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Check if cache exists and is valid
   */
  has(key: string, ttl: number = this.DEFAULT_TTL): boolean {
    return this.get(key, ttl) !== null;
  }

  /**
   * Remove specific cache entry
   */
  remove(key: string): void {
    const locationKey = this.getLocationKey(key);
    this.cache.delete(locationKey);
  }

  /**
   * Clear specific cache keys
   */
  clearKeys(keys: string[]): void {
    keys.forEach(key => {
      const locationKey = this.getLocationKey(key);
      this.cache.delete(locationKey);
    });
  }

  /**
   * Clear all cache for current location
   */
  clearLocationCache(): void {
    if (this.currentLocationId === null) {
      return;
    }

    const prefix = `loc:${this.currentLocationId}:`;
    const keysToDelete: string[] = [];

    this.cache.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
    console.log(`Cleared ${keysToDelete.length} cache entries for location ${this.currentLocationId}`);
  }

  /**
   * Clear all cache
   */
  clearAll(): void {
    this.cache.clear();
  }

  /**
   * Get age of cache in milliseconds
   */
  getCacheAge(key: string): number | null {
    const locationKey = this.getLocationKey(key);
    const item = this.cache.get(locationKey);
    if (!item) {
      return null;
    }
    return Date.now() - item.timestamp;
  }

  /**
   * Get formatted cache age for display
   */
  getFormattedCacheAge(key: string): string | null {
    const age = this.getCacheAge(key);
    if (age === null) {
      return null;
    }

    const seconds = Math.floor(age / 1000);
    if (seconds < 60) {
      return `${seconds}s ago`;
    }

    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  }
}
